import { useState } from 'react';
import type { Loan, LoanEvent, LoanEventType } from '@/types';
import { EVENT_TYPE_LABELS, EVENT_TYPE_DESCRIPTIONS } from '@/types';
import { EVENT_TYPE_ICONS } from '../eventDisplay';
import { EventForm } from './EventForm';

interface EventSheetProps {
  loan: Loan;
  /** Every currently-active event for the loan — needed by the repayment-consequence basis calculation. */
  events: LoanEvent[];
  /** If provided, opens directly into edit mode for this event, skipping the type picker. */
  editingEvent?: LoanEvent;
  onClose: () => void;
  onSaved: () => void;
}

const EVENT_TYPE_ORDER: LoanEventType[] = [
  'disbursement',
  'rate_change',
  'prepayment',
  'balance_correction',
  'charge',
  'emi_date_change',
];

/**
 * The single "Add Event" entry point (spec: "Single Add Event flow").
 * Step 1: pick an event type from a grid. Step 2: fill in that type's
 * form. When editing an existing event, step 1 is skipped since the
 * type is already fixed (an event's type never changes across an edit —
 * see eventsRepo.editEvent's Omit<..., 'type'>).
 */
export function EventSheet({ loan, events, editingEvent, onClose, onSaved }: EventSheetProps) {
  const [selectedType, setSelectedType] = useState<LoanEventType | undefined>(editingEvent?.type);

  function handleSaved() {
    onSaved();
    onClose();
  }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle" />
        <div className="sheet-header">
          <h2 style={{ margin: 0 }}>
            {editingEvent ? `Edit ${EVENT_TYPE_LABELS[editingEvent.type]}` : selectedType ? EVENT_TYPE_LABELS[selectedType] : 'Add Event'}
          </h2>
          <button className="close-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        {!selectedType && (
          <div className="event-type-grid">
            {EVENT_TYPE_ORDER.map((type) => (
              <button key={type} type="button" className="event-type-option" onClick={() => setSelectedType(type)}>
                <span className="icon">{EVENT_TYPE_ICONS[type]}</span>
                <span className="label">{EVENT_TYPE_LABELS[type]}</span>
              </button>
            ))}
          </div>
        )}

        {selectedType && (
          <div className="stack">
            {!editingEvent && <p style={{ marginTop: 0 }}>{EVENT_TYPE_DESCRIPTIONS[selectedType]}</p>}
            <EventForm
              loan={loan}
              events={events}
              eventType={selectedType}
              existingEvent={editingEvent}
              onSaved={handleSaved}
              onCancel={editingEvent ? onClose : () => setSelectedType(undefined)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
